import { v4 as uuidv4 } from 'uuid';
import { isQuestionVisible } from './conditionEvaluator';
import { questionAllowsAdvance } from './fillLogic';
class Question {
  constructor({prompt, field, required, helpText, value, builderSettings, displayCondition, requiredCondition}) {
    const questionType = this.constructor.questionType();
    const questionTypeHumanReadable = this.constructor.questionTypeHumanReadable() || questionType;
    if (!questionType) {
      throw new Error('missing "questionType". You are likely trying to instantiate an abstract Question Class');
    }
    if (!prompt && prompt !== "") {
      throw new Error('"prompt" is required when creating a Question');
    }
    this.id = uuidv4();
    this.questionType = questionType;
    this.questionTypeHumanReadable = questionTypeHumanReadable;
    this.prompt = prompt;
    this.field = field || prompt;
    this.required = !!required;
    this.helpText = helpText;
    this.builderSettings = builderSettings || {};
    // Conditional-logic envelopes (ULLR-150), mirroring the backend Question
    // model: absent = always visible / static `required` applies.
    this.displayCondition = displayCondition;
    this.requiredCondition = requiredCondition;
    this.value = value;
  }

  clone() {
    const newQ = new this.constructor({...this});
    newQ.id = this.id;
    return newQ;
  }

  setRequired(newValue) {
    this.required = !!newValue;
  }

  // returns whether the question has a sufficient value such that it can be considered complete
  // 0 = not complete
  // 0.5 = partial
  // 1 = complete
  isComplete() {
    const readableValue = this.value;
    if (!readableValue) {
      return 0;
    }
    return readableValue ? 1 : 0;
  }

  static questionType() {
    return undefined;
  }

  static createBlankQuestion() {
    return new this.prototype.constructor({
      prompt: 'New Question',
      required: false,
    }) 
  }
}

class DateTimeQuestion extends Question {
  static questionType() {
    return 'dateTime';
  }

  static questionTypeHumanReadable() {
    return 'Date & time';
  }
}

class ShortAnswerQuestion extends Question {
  static questionType() {
    return 'shortAnswer';
  }

  static questionTypeHumanReadable() {
    return 'Short text';
  }
}

class LongAnswerQuestion extends Question {
  static questionType() {
    return 'longAnswer';
  }

  static questionTypeHumanReadable() {
    return 'Long text';
  }
}

class SignatureQuestion extends Question {
  static questionType() {
    return 'signature';
  }

  static questionTypeHumanReadable() {
    return 'Signature';
  }
}

	
class GeographicLocationQuestion extends Question {
  static questionType() {
    return 'geographicLocation';
  }
  static questionTypeHumanReadable() {
    return 'Location';
  }
}

class MarkdownQuestion extends Question {
  constructor(props) {
    super({
      ...props,
      required: false,
      value: undefined,
    });
  }

  setRequired(newValue) {
    this.required = false; // this can never be required. It will never have a value
  }

  isComplete() {
    return 1; // no value allowed, so it's always "complete"
  }

  static questionType() {
    return 'markdown';
  }

  static questionTypeHumanReadable() {
    return 'Instructions / Info';
  }
}

class ImagesQuestion extends Question {
  constructor({prompt, field, required, minAnswers, maxAnswers, helpText, value, displayCondition, requiredCondition}) {
    super({prompt, field, required, helpText, value, displayCondition, requiredCondition});
    this.minAnswers = minAnswers;
    this.maxAnswers = maxAnswers;
  }

  setRequired(newValue) {
    this.required = !!newValue;
    // if required, make minAnswers 1
    if (this.required && !this.minAnswers) {
      this.minAnswers = 1;
    }
  }

  static questionType() {
    return 'images';
  }

  static questionTypeHumanReadable() {
    return 'Images';
  }

  // returns whether the question has a sufficient value such that it can be considered complete
  // 0 = not complete
  // 0.5 = partial
  // 1 = complete
  isComplete() {
    const readableValue = this.value;
    if (!readableValue) {
      return 0;
    }
    // determine whether the number of selected answers >= minAnswers and <= maxAnswers
    const isComplete = (this.minAnswers && readableValue.length >= this.minAnswers)
      && (this.maxAnswers == null || (this.maxAnswers && readableValue.length <= this.maxAnswers))
      && !!readableValue.length;
    return isComplete ? 1 : 0;
  }
}

class MeasurementQuestionAbstract extends Question {
  // value has two parts: "number" and "unit"
  constructor({prompt, field, required, helpText, value, displayCondition, requiredCondition}) {
    super({prompt, field, required, helpText, value, displayCondition, requiredCondition});
    const measurementType = this.constructor.measurementType();
    const units = this.constructor.units();
    this.measurementType = measurementType;
    this.units = units;
  }

  static questionType() {
    return 'measurementAbstract';
  }
}

class LengthMeasurementQuestion extends MeasurementQuestionAbstract {
  static questionType() {
    return 'lengthMeasurement';
  }

  static questionTypeHumanReadable() {
    return 'Length measurement';
  }

  static measurementType() {
    return 'length';
  }

  static units() {
    return ['ft', 'm'];
  }
}

class AngleMeasurementQuestion extends MeasurementQuestionAbstract {
  static questionType() {
    return 'angleMeasurement';
  }

  static questionTypeHumanReadable() {
    return 'Angle measurement';
  }

  static measurementType() {
    return 'angle';
  }

  static units() {
    return ['deg', 'rad'];
  }
}

class NumberQuestion extends Question {
  // value is a plain number. `min`/`max` are an ADVISORY acceptable range —
  // the fill UI warns when the answer falls outside it but never blocks
  // submission. Config is FLAT wire fields (unit/decimals/min/max), mirroring
  // the backend model (ULLR-150 ratified contract — not a nested `config`).
  constructor({prompt, field, required, unit, decimals, min, max, helpText, value, displayCondition, requiredCondition}) {
    super({prompt, field, required, helpText, value, displayCondition, requiredCondition});
    this.unit = unit;
    this.decimals = !!decimals;
    this.min = min;
    this.max = max;
  }

  static questionType() {
    return 'number';
  }

  static questionTypeHumanReadable() {
    return 'Number';
  }

  // AUTHORING default only (the design's blank number question is Decimals).
  // The WIRE default is untouched: an absent `decimals` still deserializes to
  // false via the constructor.
  static createBlankQuestion() {
    return new this.prototype.constructor({
      prompt: 'New Question',
      required: false,
      decimals: true,
    });
  }

  // returns whether the question has a sufficient value such that it can be considered complete
  // 0 = not complete
  // 1 = complete
  // NOTE: an answer of 0 IS complete — the base class's falsy check would
  // wrongly block a required number answered "0".
  isComplete() {
    if (this.value === undefined || this.value === null || this.value === '') {
      return 0;
    }
    return Number.isFinite(Number(this.value)) ? 1 : 0;
  }
}

class SlopeMeasurementQuestion extends Question {
  static questionType() {
    return 'slopeMeasurement';
  }

  static questionTypeHumanReadable() {
    return 'Slope measurement';
  }

  // returns whether the question has a sufficient value such that it can be considered complete
  // 0 = not complete
  // 0.5 = partial
  // 1 = complete
  isComplete() {
    const readableValue = this.value;
    if (!readableValue) {
      return 0;
    }
    // determine whether > 0 segements have been recorded
    // and whether the last segment is 0 degrees
    const hasSegments = Array.isArray(readableValue) && !!readableValue.length;
    const lastSegment = hasSegments && readableValue[readableValue.length - 1];
    const lastSegmentIsZeroDegrees = lastSegment && lastSegment.angle && lastSegment.angle.number === 0; // this works for radian and degrees
    if (hasSegments && !lastSegmentIsZeroDegrees) {
      return 0.5;
    }
    return hasSegments && lastSegmentIsZeroDegrees ? 1 : 0;
  }
}

/// // SELECT QUESTIONS
class SelectQuestionOption {
  constructor({display, value, icon, iconSet, builderSettings}) {
    const validIconSets = ['material-community', 'weather'];
    if (!display && display !== "") {
      throw new Error('"display" is required when creating a SelectQuestionOption');
    }
    if (iconSet && !validIconSets.includes(iconSet)) {
      throw new Error('invalid "iconSet" when creating a SelectQuestionOption');
    }
    this.id = uuidv4();
    this.display = display;
    this.value = value || display;
    this.icon = icon;
    this.iconSet = iconSet || validIconSets[0];
    this.builderSettings = builderSettings || {};
  }

  clone() {
    const newOption = new SelectQuestionOption(this);
    newOption.id = this.id;
    return newOption;
  }

  static factory(rawObject) {
    const newOption =  new SelectQuestionOption({
      display: rawObject.display,
      value: rawObject.value,
      icon: rawObject.icon,
      iconSet: rawObject.iconSet,
    });
    if (rawObject.id) {
      newOption.id = rawObject.id;
    }
    return newOption;
  }
}

/**
 * This is meant to be an abstract parent class for all select questions.
 * - Some select questions will have pre-defined options. These will be "standard" select questions
 *   of the standard `SelectQuestion` class.
 * - Some will have options that are generated dynamically. These will be "dynamic" select questions
 */
class AbstractSelectQuestion extends Question {
  constructor({prompt, field, required, minAnswers, maxAnswers, options, optionsPerRow, helpText, value, displayCondition, requiredCondition}) {
    if (maxAnswers && minAnswers > maxAnswers) {
      throw new Error('"minAnswers" must be <= "maxAnswers" when creating a SelectQuestion');
    }
    if (!options.every((o) => o instanceof SelectQuestionOption)) {
      throw new Error('all "options" must be an instance of SelectQuestionOption');
    }
    super({prompt, field, required, helpText, value, displayCondition, requiredCondition});
    this.minAnswers = minAnswers;
    this.maxAnswers = maxAnswers || options.length;
    if (this.maxAnswers > options.length) {
      this.maxAnswers = options.length;
    }
    this.options = options;
    this.optionsPerRow = optionsPerRow;
  }

  setRequired(newValue) {
    this.required = !!newValue;
    // if required, make minAnswers 1
    if (this.required && !this.minAnswers) {
      this.minAnswers = 1;
    } else if (!this.required) {
      this.minAnswers = 0;
    }
  }

  addOption(questionOption) {
    if (!this.options) {
      this.options = [];
    }
    const isMaxAllOptions = this.maxAnswers === this.options.length;
    this.options.push(questionOption);
    if (isMaxAllOptions) {
      this.maxAnswers = this.options.length;
    }
  }

  removeOption(optionIndex) {
    if (!this.options) {
      this.options = [];
    }
    // remove option
    if (this.options[optionIndex]) {
      this.options.splice(optionIndex, 1);
    }
    // correct max Answers if necessary
    if (this.maxAnswers > this.options.length) {
      this.maxAnswers = this.options.length
    }
  }

  static questionType() {
    return 'select abstract';
  }

  static questionTypeHumanReadable() {
    return 'Select Abstract';
  }

  static createBlankQuestion() {
    return new this.prototype.constructor({
      prompt: 'New Question',
      required: false,
      minAnswers: 0,
      maxAnswers: null,
      options: [
        new SelectQuestionOption({
          display: 'Option 1',
        }),
        new SelectQuestionOption({
          display: 'Option 2',
        }),
      ],
    }) 
  }

  // returns whether the question has a sufficient value such that it can be considered complete
  // 0 = not complete
  // 0.5 = partial
  // 1 = complete
  isComplete() {
    const readableValue = Array.isArray(this.value) 
      ? this.value
      : [this.value].filter((v) => !!v);
    if (!readableValue) {
      return 0;
    }
    // determine whether the number of selected answers >= minAnswers and <= maxAnswers
    const isComplete = (this.minAnswers && readableValue.length >= this.minAnswers)
      && (this.maxAnswers == null || (this.maxAnswers && readableValue.length <= this.maxAnswers))
      && !!readableValue.length;
    return isComplete ? 1 : 0;
  }
}

class SelectQuestion extends AbstractSelectQuestion {
  constructor({prompt, field, required, minAnswers, maxAnswers, options, optionsPerRow, helpText, value, displayCondition, requiredCondition}) {
    if (!options || !Array.isArray(options) || !options.length) {
      throw new Error('an array of 1 or more "options" is required when creating a SelectQuestion');
    }
    super({prompt, field, required, minAnswers, maxAnswers, options, optionsPerRow, helpText, value, displayCondition, requiredCondition});
  }

  static questionType() {
    return 'select';
  }

  static questionTypeHumanReadable() {
    return 'Select multiple';
  }

  static createBlankQuestion() {
    return new this.prototype.constructor({
      prompt: 'New Question',
      required: false,
      minAnswers: 0,
      maxAnswers: null,
      options: [
        new SelectQuestionOption({
          display: 'Option 1',
        }),
        new SelectQuestionOption({
          display: 'Option 2',
        }),
      ],
    }) 
  }
}

class SelectByFeatureQuestion extends SelectQuestion {
  /* 
    v6 <= v < v9 (OLD)
    value should be a map of featureId to standard select question value array
    {
      featureId1: ["valueA", "valueB"],
      featureId2: ["valueB", "valueC", "valueD"],
      ...
    }
  */
 /* 
    v9 <= v  (NEW)
    value should be a map of featureId to standard select question value array
    {
      featureId1: ["valueA", "valueB"],
      featureId2: ["valueB", "valueC", "valueD"],
      ...
    }
  */
  constructor({prompt, field, required, minAnswers, maxAnswers, options, optionsPerRow, helpText, value, displayCondition, requiredCondition}) {
    super({prompt, field, required, minAnswers, maxAnswers, options, optionsPerRow, helpText, value, displayCondition, requiredCondition});
  }
  static questionType() {
    return 'selectByFeature';
  }

  static questionTypeHumanReadable() {
    return 'Select by feature';
  }

  // returns whether the question has a sufficient value such that it can be considered complete
  // 0 = not complete
  // 0.5 = partial
  // 1 = complete
  isComplete() {
    const readableValue = this.value;
    if (!readableValue) {
      return 0;
    }
    // determine whether at least one entity has at least one selected value
    if (readableValue && Array.isArray(readableValue)) {
      for (const entityValue of readableValue) {
        const selectedValuesForEntity = entityValue.value;
        if (selectedValuesForEntity && selectedValuesForEntity.length) {
          return 1;
        }
      }
    }
  }
}

class SelectFromListQuestion extends SelectQuestion {
  static questionType() {
    return 'selectFromList';
  }

  static questionTypeHumanReadable() {
    return 'Select from list';
  }
}

class SelectUsersQuestion extends AbstractSelectQuestion {
  constructor({prompt, field, required, minAnswers, maxAnswers, userGroupIds, options, optionsPerRow, helpText, value, displayCondition, requiredCondition}) {
    super({
      prompt,
      field,
      required,
      minAnswers,
      maxAnswers,
      options: options || [], // we have to allow for options here because we may be getting a hydrated form
      optionsPerRow,
      helpText,
      value,
      displayCondition,
      requiredCondition,
    });
    this.userGroupIds = userGroupIds || undefined;
  }
  static questionType() {
    return 'selectUsers';
  }

  static questionTypeHumanReadable() {
    return 'Select users';
  }

  static createBlankQuestion() {
    return new this({
      prompt: 'New Question',
      required: false,
      minAnswers: 0,
      maxAnswers: undefined,
    }) 
  }
}
class SelectSingleQuestion extends SelectQuestion {
  constructor(props) {
    super({
      ...props,
      minAnswers: props?.required ? 1 : 0,
      maxAnswers: 1,
    });
  }

  static questionType() {
    return 'selectSingle';
  }

  static questionTypeHumanReadable() {
    return 'Select single';
  }
}



class QuestionFactory {
  static factory(rawObject) {
    let newQuestion;
    switch (rawObject.questionType) {
      case DateTimeQuestion.questionType():
        newQuestion = new DateTimeQuestion({...rawObject});
        if (rawObject.id) {
          newQuestion.id = rawObject.id;
        }
        return newQuestion;
      case ShortAnswerQuestion.questionType():
        newQuestion = new ShortAnswerQuestion({...rawObject});
        if (rawObject.id) {
          newQuestion.id = rawObject.id;
        }
        return newQuestion;
      case LongAnswerQuestion.questionType():
        newQuestion = new LongAnswerQuestion({...rawObject});
        if (rawObject.id) {
          newQuestion.id = rawObject.id;
        }
        return newQuestion;
      case GeographicLocationQuestion.questionType():
        newQuestion = new GeographicLocationQuestion({...rawObject});
        if (rawObject.id) {
          newQuestion.id = rawObject.id;
        }
        return newQuestion;
      case MarkdownQuestion.questionType():
        newQuestion = new MarkdownQuestion({...rawObject});
        if (rawObject.id) {
          newQuestion.id = rawObject.id;
        }
        return newQuestion;
      case ImagesQuestion.questionType():
        newQuestion = new ImagesQuestion({...rawObject});
        if (rawObject.id) {
          newQuestion.id = rawObject.id;
        }
        return newQuestion;
      case SignatureQuestion.questionType():
        newQuestion = new SignatureQuestion({...rawObject});
        if (rawObject.id) {
          newQuestion.id = rawObject.id;
        }
        return newQuestion;
      case LengthMeasurementQuestion.questionType():
        newQuestion = new LengthMeasurementQuestion({...rawObject});
        if (rawObject.id) {
          newQuestion.id = rawObject.id;
        }
        return newQuestion;
      case AngleMeasurementQuestion.questionType():
        newQuestion = new AngleMeasurementQuestion({...rawObject});
        if (rawObject.id) {
          newQuestion.id = rawObject.id;
        }
        return newQuestion;
      case SlopeMeasurementQuestion.questionType():
        newQuestion = new SlopeMeasurementQuestion({...rawObject});
        if (rawObject.id) {
          newQuestion.id = rawObject.id;
        }
        return newQuestion;
      case NumberQuestion.questionType():
        newQuestion = new NumberQuestion({...rawObject});
        if (rawObject.id) {
          newQuestion.id = rawObject.id;
        }
        return newQuestion;
      case SelectQuestion.questionType():
        newQuestion = new SelectQuestion({
          ...rawObject,
          options: rawObject.options.map((qs) => SelectQuestionOption.factory(qs)),
        });
        if (rawObject.id) {
          newQuestion.id = rawObject.id;
        }
        return newQuestion;
      case SelectFromListQuestion.questionType():
        newQuestion = new SelectFromListQuestion({
          ...rawObject,
          options: rawObject.options.map((qs) => SelectQuestionOption.factory(qs)),
        });
        if (rawObject.id) {
          newQuestion.id = rawObject.id;
        }
        return newQuestion;
      case SelectSingleQuestion.questionType():
        newQuestion = new SelectSingleQuestion({
          ...rawObject,
          options: rawObject.options.map((qs) => SelectQuestionOption.factory(qs)),
        });
        if (rawObject.id) {
          newQuestion.id = rawObject.id;
        }
        return newQuestion;
      case SelectByFeatureQuestion.questionType():
        newQuestion = new SelectByFeatureQuestion({
          ...rawObject,
          options: rawObject.options.map((qs) => SelectQuestionOption.factory(qs)),
        });
        if (rawObject.id) {
          newQuestion.id = rawObject.id;
        }
        return newQuestion;
      case SelectUsersQuestion.questionType():
        newQuestion = new SelectUsersQuestion({
          ...rawObject,
          options: rawObject.options.map((qs) => SelectQuestionOption.factory(qs)),
        });
        if (rawObject.id) {
          newQuestion.id = rawObject.id;
        }
        return newQuestion;
      default:
        break;
    }
  }
}

/// // Question Set
class QuestionSet {
  constructor({name, questions, helpText, builderSettings}) {
    if (!name && name !== "") {
      throw new Error("missing 'name' when creating QuestionSet");
    }
    if (!questions) {
      throw new Error('an array of 0 or more "questions" is required when creating a QuestionSet');
    }
    if (!questions.every((o) => o instanceof Question)) {
      console.error(`Error with question set: ${JSON.stringify(questions, null, 2)}`)
      throw new Error('all "questions" must be an instance of Question');
    }
    this.id = uuidv4();
    this.name = name;
    this.questions = questions;
    this.helpText = helpText;
    this.builderSettings = builderSettings || {};
  }

  clone() {
    const newQs = new this.constructor({...this});
    newQs.id = this.id;
    return newQs;
  }

  static factory(rawObject) {
    const newQuestionSet = new QuestionSet({
      name: rawObject.name,
      questions: rawObject.questions.map((o) => QuestionFactory.factory(o)),
      helpText: rawObject.helpText,
    });
    if (rawObject.id) {
      newQuestionSet.id = rawObject.id;
    }
    return newQuestionSet;
  }

  /**
   * ULLR-150: completeness over the VISIBLE questions of this set.
   * `completedForm` (the whole form, all pages) provides cross-page condition
   * context; omitted = this set alone (no cross-page context, matching legacy
   * callers). Was a getter — converted to a method for the context argument
   * (no external callers existed).
   */
  completionPercentage(completedForm = undefined) {
    let questions = this.questions;
    // Depending on the Parent Slope measurement strategy, we only need
    // answers to a subset of questions
    if (this.name === 'Parent Slope' && this.questions[0].field === 'Measurement Strategy') {
      const measurementStrategyQuestion = this.questions[0];
      const measurementStrategyValue = measurementStrategyQuestion && measurementStrategyQuestion.value && measurementStrategyQuestion.value.readableValue;
      const measurementStrategy = measurementStrategyValue && measurementStrategyValue[0];
      if (measurementStrategy === 'Linear') {
        questions = this.questions.filter((q) => !['l2', 'theta2'].includes(q.field));
      }
    }

    // Depending on the Parent Slope measurement strategy, we only need
    // answers to a subset of questions
    if (this.name === 'Width' && this.questions[0].field === 'Measurement Strategy') {
      const measurementStrategyQuestion = this.questions[0];
      const measurementStrategyValue = measurementStrategyQuestion && measurementStrategyQuestion.value && measurementStrategyQuestion.value.readableValue;
      const measurementStrategy = measurementStrategyValue && measurementStrategyValue[0];
      if (measurementStrategy === 'One Takeoff') {
        questions = this.questions.filter((q) => q.field !== 'widthSecondaryTakeoff');
      }
    }

    // condition-hidden questions don't count toward completeness
    const conditionContext = completedForm || { questionSets: [this] };
    questions = questions.filter((q) => isQuestionVisible(q, conditionContext));

    // determine the number of questions with values
    const numberOfQuestions = questions.length;
    if (!numberOfQuestions) {
      return 1;
    }
    let questionCompleteSum = 0;

    for (const question of questions) {
      questionCompleteSum += question.isComplete();
    }

    return questionCompleteSum / numberOfQuestions;
  }

  /**
   * ULLR-150: gate on evaluator-COMPUTED requiredness (hidden ⇒ never
   * required/never gates; requiredCondition overrides static `required`).
   * `completedForm` provides cross-page condition context; omitted = this set
   * alone. The per-question rule lives in the shared `questionAllowsAdvance`
   * helper, which the builder's preview modal also uses — preview gating IS
   * fill gating.
   */
  shouldEnableNext(completedForm = undefined) {
    const conditionContext = completedForm || { questionSets: [this] };
    return this.questions.every((question) => questionAllowsAdvance(question, conditionContext));
  }
}

export {
  Question,
  DateTimeQuestion,
  ShortAnswerQuestion,
  LongAnswerQuestion,
  GeographicLocationQuestion,
  MarkdownQuestion,
  ImagesQuestion,
  SignatureQuestion,
  LengthMeasurementQuestion,
  AngleMeasurementQuestion,
  SlopeMeasurementQuestion,
  NumberQuestion,
  SelectQuestion,
  SelectFromListQuestion,
  SelectUsersQuestion,
  SelectSingleQuestion,
  SelectByFeatureQuestion,
  SelectQuestionOption,
  QuestionSet,
  QuestionFactory,
};
